import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { TagCategory } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import { getCompanyContacts } from '../../../../domain/use_cases/contacts/getCompanyContacts';
import { getCompanyOrphanContacts } from '../../../../domain/use_cases/contacts/getCompanyOrphanContacts';
import { attachOrphanContacts } from '../../../../domain/use_cases/contacts/attachOrphanContacts';
import { CompanyContact, ContactSubscription, ContactTag } from '../../../../domain/models/Contact';
import {
  CONTACTS_LAST_INTERACTION_OPTIONS,
  CONTACTS_SORT_OPTIONS,
  ContactsFilterChip,
  ContactsFilters,
  ContactsSortBy,
  DEFAULT_CONTACTS_FILTERS,
  buildContactDisplay,
  filterContactDisplays,
  buildSubscriptionOptions,
  groupContactsByHeat,
  groupContactsByLetter,
  groupContactsByType,
  sortContactDisplays,
} from '../types/ContactDisplay';

const ALL_SUBSCRIPTIONS_LABEL = "Toute l'entreprise";
// Scope of the contacts no number shows any more: their owner's number went to someone else without them
export const ORPHANS_SCOPE = 'orphans';
const LOCAL_STORAGE_DEFAULT_SORT_KEY = 'contacts_default_sort';
const NO_ORPHANS: CompanyContact[] = [];
const NO_OWNER_NAMES: Record<string, string> = {};

// Default sort starred by the user, kept on this device only
const getStoredDefaultSort = (): ContactsSortBy => {
  const stored = localStorage.getItem(LOCAL_STORAGE_DEFAULT_SORT_KEY);
  return CONTACTS_SORT_OPTIONS.find((option) => option.value === stored)?.value ?? DEFAULT_CONTACTS_FILTERS.sortBy;
};

const useContactsPageController = () => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany, users } = useContext(CompanyContext);

  const [contacts, setContacts] = useState<CompanyContact[]>([]);
  // Orphans of the company they were read for: another company never shows them while its own load
  const [loadedOrphans, setOrphans] = useState<{ companyId: string; contacts: CompanyContact[]; ownerNames: Record<string, string> }>({ companyId: '', contacts: NO_ORPHANS, ownerNames: NO_OWNER_NAMES });
  const [isAttaching, setIsAttaching] = useState(false);
  const [tags, setTags] = useState<ContactTag[]>([]);
  const [subscriptions, setSubscriptions] = useState<ContactSubscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [defaultSortBy, setDefaultSortByState] = useState<ContactsSortBy>(getStoredDefaultSort);
  const [filters, setFilters] = useState<ContactsFilters>(() => ({ ...DEFAULT_CONTACTS_FILTERS, sortBy: defaultSortBy }));
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);

  // Bumped on every fetch: a page landing after the company changed belongs to a stale fetch
  const fetchIdRef = useRef(0);

  const stopFetching = useCallback(() => {
    fetchIdRef.current += 1;
  }, []);

  const companyId = activeCompany?.id;
  const isSupervisor = !!activeCompany?.isSupervisor;
  // A member never sees orphans, even those loaded while a supervised company was active
  const orphans = isSupervisor && loadedOrphans.companyId === companyId ? loadedOrphans.contacts : NO_ORPHANS;
  const orphanOwnerNames = isSupervisor && loadedOrphans.companyId === companyId ? loadedOrphans.ownerNames : NO_OWNER_NAMES;

  // First page shown as soon as it lands, the following ones appended in the background until every line is exhausted
  const fetchContacts = useCallback(async () => {
    if (!companyId || !authentication) return;

    const fetchId = ++fetchIdRef.current;
    const isStale = () => fetchId !== fetchIdRef.current;

    setIsLoading(true);
    setIsLoadingMore(false);
    try {
      let response = await getCompanyContacts({ company_id: companyId }, authentication);
      if (isStale()) return;
      setContacts(response.contacts);
      setTags(response.tags ?? []);
      setSubscriptions(response.subscriptions ?? []);
      setIsLoading(false);

      while (response.next_cursors !== null) {
        setIsLoadingMore(true);
        response = await getCompanyContacts({ company_id: companyId, cursors: response.next_cursors }, authentication);
        if (isStale()) return;
        const page = response.contacts;
        // A line whose cursor contact was deleted restarts from its first page: skip the contacts already loaded
        setContacts((previous) => {
          const loadedIds = new Set(previous.map((contact) => contact.id));
          return [...previous, ...page.filter((contact) => !loadedIds.has(contact.id))];
        });
      }
    } catch (e) {
      if (isStale()) return;
      const errorMessage = e instanceof Error ? e.message : 'Erreur lors du chargement des contacts';
      message.error(errorMessage);
    } finally {
      if (!isStale()) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [companyId, authentication]);

  useEffect(() => {
    // Synchro avec une source distante : le setState synchrone est le passage en chargement qui precede l'appel.
    // eslint-disable-next-line react/set-state-in-effect
    fetchContacts();
    // Stops the background paging when the page unmounts
    return stopFetching;
  }, [fetchContacts, stopFetching]);

  // Supervisors only: the orphan contacts of the members, shown under their own scope
  const fetchOrphans = useCallback(async () => {
    if (!companyId || !authentication || !isSupervisor) return;
    try {
      setOrphans({ companyId, ...await getCompanyOrphanContacts({ company_id: companyId }, authentication) });
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Erreur lors du chargement des contacts orphelins');
    }
  }, [companyId, authentication, isSupervisor]);

  useEffect(() => {
    // Synchro avec une source distante, comme le chargement des contacts.
    // eslint-disable-next-line react/set-state-in-effect
    void fetchOrphans();
  }, [fetchOrphans]);

  const userNameBySubscription = useMemo(() => {
    const names = new Map<string, string>();
    users.forEach((user) => {
      const userName = `${user.firstname} ${user.lastname}`.trim() || user.email || '';
      Object.keys(user.idSubscription || {}).forEach((subscriptionId) => names.set(subscriptionId, userName));
    });
    return names;
  }, [users]);

  const userNameByUid = useMemo(
    () => new Map(users.map((user) => [user.uid, `${user.firstname} ${user.lastname}`.trim() || user.email || ''])),
    [users],
  );

  // An owner by his name, a former member by his name too: only a deleted account has none
  const orphanOwnerOf = useCallback((uid: string) => {
    const name = userNameByUid.get(uid) || orphanOwnerNames[uid] || '';
    return {
      name: name || 'Compte supprimé',
      ofName: !name ? "d'un compte supprimé" : /^[aeiouyàâäéèêëîïôöùûü]/i.test(name) ? `d'${name}` : `de ${name}`,
      hasLeft: !userNameByUid.has(uid) && !!name,
    };
  }, [userNameByUid, orphanOwnerNames]);

  const tagsById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag])), [tags]);

  const isOrphansScope = filters.subscriptionId === ORPHANS_SCOPE;

  // An orphan has no line: it is followed by the member it still belongs to
  const displayedContacts = useMemo(
    () => isOrphansScope
      ? orphans.map((contact) => ({
        ...buildContactDisplay(contact, tagsById, subscriptions, userNameBySubscription),
        followedBy: orphanOwnerOf(contact.contact?.owner ?? '').name,
      }))
      : contacts.map((contact) => buildContactDisplay(contact, tagsById, subscriptions, userNameBySubscription)),
    [isOrphansScope, orphans, contacts, tagsById, subscriptions, userNameBySubscription, orphanOwnerOf],
  );

  const filteredContacts = useMemo(
    () => sortContactDisplays(
      filterContactDisplays(displayedContacts, isOrphansScope ? { ...filters, subscriptionId: null } : filters),
      filters.sortBy,
    ),
    [displayedContacts, filters, isOrphansScope],
  );

  // One entry per member whose contacts are orphan, to put them back on a number
  const orphanOwners = useMemo(() => {
    const countByOwner = new Map<string, number>();
    orphans.forEach((contact) => {
      const ownerUid = contact.contact?.owner ?? '';
      countByOwner.set(ownerUid, (countByOwner.get(ownerUid) ?? 0) + 1);
    });
    return [...countByOwner.entries()].map(([uid, count]) => ({ uid, ...orphanOwnerOf(uid), count }));
  }, [orphans, orphanOwnerOf]);

  // Groups follow the order: letters for A-Z, levels for heat, typologies for type, a single list for recent
  const groups = useMemo(() => {
    if (filters.sortBy === 'alphabetical') return groupContactsByLetter(filteredContacts);
    if (filters.sortBy === 'heat') return groupContactsByHeat(filteredContacts);
    if (filters.sortBy === 'type') return groupContactsByType(filteredContacts);
    return [{ letter: '', contacts: filteredContacts }];
  }, [filteredContacts, filters.sortBy]);

  const tagOptions = useCallback(
    (category: TagCategory) =>
      tags.filter((tag) => tag.category === category).map((tag) => ({ value: tag.id, label: tag.name })),
    [tags],
  );

  const subscriptionOptions = useMemo(
    () => [
      // Contacts whose member holds no number any more, first so a supervisor sees them
      ...(orphans.length > 0 ? [{ value: ORPHANS_SCOPE, label: `Sans numéro (${orphans.length})` }] : []),
      ...buildSubscriptionOptions(subscriptions, userNameBySubscription, ALL_SUBSCRIPTIONS_LABEL),
    ],
    [subscriptions, userNameBySubscription, orphans.length],
  );

  // Orphans join a number someone holds: that holder becomes their owner
  const attachTargets = useMemo(
    () => subscriptions
      .filter((subscription) => userNameBySubscription.has(subscription.id))
      .map((subscription) => ({ value: subscription.id, label: userNameBySubscription.get(subscription.id) ?? '', name: subscription.name })),
    [subscriptions, userNameBySubscription],
  );

  const attachOrphans = async (ownerUid: string, subscriptionId: string) => {
    if (!companyId || !authentication) return;
    setIsAttaching(true);
    try {
      const attached = await attachOrphanContacts({ company_id: companyId, owner_uid: ownerUid, subscription_id: subscriptionId }, authentication);
      message.success(`${attached} contact${attached > 1 ? 's rattachés' : ' rattaché'}`);
      if (orphans.every((contact) => contact.contact?.owner === ownerUid)) {
        setFilters((previous) => ({ ...previous, subscriptionId: null }));
      }
      await Promise.all([fetchOrphans(), fetchContacts()]);
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Erreur lors du rattachement des contacts');
    } finally {
      setIsAttaching(false);
    }
  };

  const updateFilter = <K extends keyof ContactsFilters>(key: K) => (value: ContactsFilters[K]) =>
    setFilters((previous) => ({ ...previous, [key]: value }));

  // One chip per chosen value of the dropdown filters; a tag no longer known has no chip
  const filterChips = useMemo<ContactsFilterChip[]>(() => {
    const tagChips = (['heat', 'types', 'tags'] as const).flatMap((key) =>
      filters[key]
        .filter((tagId) => tagsById.has(tagId))
        .map((tagId) => ({ key, value: tagId, label: tagsById.get(tagId)?.name ?? '' })),
    );
    const lastInteractionLabel = CONTACTS_LAST_INTERACTION_OPTIONS.find((option) => option.value === filters.lastInteraction)?.label;
    return filters.lastInteraction === 'any' || !lastInteractionLabel
      ? tagChips
      : [...tagChips, { key: 'lastInteraction', value: filters.lastInteraction, label: lastInteractionLabel }];
  }, [filters, tagsById]);

  const removeFilterChip = (chip: ContactsFilterChip) =>
    setFilters((previous) =>
      chip.key === 'lastInteraction'
        ? { ...previous, lastInteraction: DEFAULT_CONTACTS_FILTERS.lastInteraction }
        : { ...previous, [chip.key]: previous[chip.key].filter((value) => value !== chip.value) },
    );

  // Search, sort and line stay: they are not chips
  const clearFilterChips = () =>
    setFilters((previous) => ({
      ...previous,
      heat: [],
      types: [],
      tags: [],
      lastInteraction: DEFAULT_CONTACTS_FILTERS.lastInteraction,
    }));

  const setDefaultSortBy = (sortBy: ContactsSortBy) => {
    setDefaultSortByState(sortBy);
    localStorage.setItem(LOCAL_STORAGE_DEFAULT_SORT_KEY, sortBy);
  };

  const selectedContact = displayedContacts.find((contact) => contact.id === selectedContactId) ?? null;

  return {
    state: {
      groups,
      contactsCount: filteredContacts.length,
      selectedContact,
      isLoading,
      isLoadingMore,
      filters,
      defaultSortBy,
      filterChips,
      // A member reads only their own line: no scope to pick
      canSelectSubscription: subscriptions.length > 1 || orphans.length > 0,
      subscriptionOptions,
      isOrphansScope,
      orphanOwners,
      attachTargets,
      isAttaching,
      heatOptions: tagOptions(TagCategory.heat),
      typeOptions: tagOptions(TagCategory.typology),
      tagOptions: tagOptions(TagCategory.custom),
    },
    actions: {
      setSearch: updateFilter('search'),
      setSubscriptionId: (subscriptionId: string | null) => updateFilter('subscriptionId')(subscriptionId || null),
      setSortBy: updateFilter('sortBy'),
      setHeat: updateFilter('heat'),
      setTypes: updateFilter('types'),
      setTags: updateFilter('tags'),
      setLastInteraction: updateFilter('lastInteraction'),
      setDefaultSortBy,
      removeFilterChip,
      clearFilterChips,
      // An orphan has no line to read its history from
      selectContact: (contactId: string | null) => setSelectedContactId(isOrphansScope ? null : contactId),
      attachOrphans,
      refresh: fetchContacts,
    },
  };
};

export default useContactsPageController;
